import React, { createContext, useContext, useState, useCallback } from 'react';
import { mainServices, subServicesData, Service, SubService } from '../data/servicesData';

export interface SearchResult {
  type: 'service' | 'subservice';
  id: string | number;
  name: string;
  description: string;
  image: string;
  price?: string;
  priceRange?: string;
  category?: string;
  serviceId?: string; // For sub-services, to know which service they belong to
  rating?: number;
  duration?: string;
  bookingUrl?: string;
}

interface SearchContextType {
  searchQuery: string;
  searchResults: SearchResult[];
  isSearching: boolean;
  setSearchQuery: (query: string) => void;
  performSearch: (query: string) => SearchResult[];
  clearSearch: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const performSearch = useCallback((query: string): SearchResult[] => {
    if (!query.trim()) {
      return [];
    }

    setIsSearching(true);
    const searchTerm = query.toLowerCase().trim();
    const results: SearchResult[] = [];

    // Search in main services
    mainServices.forEach(service => {
      const matchesName = service.name.toLowerCase().includes(searchTerm);
      const matchesDescription = service.description.toLowerCase().includes(searchTerm);
      const matchesCategory = service.category.toLowerCase().includes(searchTerm);

      if (matchesName || matchesDescription || matchesCategory) {
        results.push({
          type: 'service',
          id: service.id,
          name: service.name,
          description: service.description,
          image: service.image,
          priceRange: service.priceRange,
          category: service.category
        });
      }
    });

    // Search in sub-services
    Object.entries(subServicesData).forEach(([serviceId, subServices]) => {
      subServices.forEach(subService => {
        const matchesName = subService.name.toLowerCase().includes(searchTerm);
        const matchesDescription = subService.description.toLowerCase().includes(searchTerm);
        
        // Search in additional fields if they exist
        const matchesMaterials = subService.materials?.some(material => 
          material.toLowerCase().includes(searchTerm)
        ) || false;
        
        const matchesIncludes = subService.includes?.some(include => 
          include.toLowerCase().includes(searchTerm)
        ) || false;
        
        const matchesBestFor = subService.bestFor?.some(purpose => 
          purpose.toLowerCase().includes(searchTerm)
        ) || false;
        
        const matchesLanguages = subService.languages?.some(language => 
          language.toLowerCase().includes(searchTerm)
        ) || false;

        // Search for common spiritual terms
        const spiritualTerms = [
          'puja', 'pujan', 'worship', 'prayer', 'mantra', 'chanting',
          'blessing', 'blessings', 'protection', 'spiritual', 'ceremony', 'ritual',
          'shiva', 'lord', 'god', 'divine', 'sacred', 'holy', 'temple',
          'abhishek', 'abhishekh', 'havan', 'yajna', 'aarti', 'arti',
          'priest', 'pandit', 'brahmin', 'vedic', 'hindu', 'hinduism',
          'ganesh', 'ganesha', 'hanuman', 'vishnu', 'lakshmi', 'durga',
          'rudra', 'mrityunjaya', 'maha', 'chalisa', 'path', 'sahasranama'
        ];
        const matchesSpiritual = spiritualTerms.some(term => {
          const termMatch = searchTerm.includes(term.toLowerCase()) || term.toLowerCase().includes(searchTerm);
          return termMatch && (matchesName || matchesDescription);
        });

        // Search in ritual steps
        const matchesRitualSteps = subService.ritualSteps?.some(step => 
          step.toLowerCase().includes(searchTerm)
        ) || false;

        if (matchesName || matchesDescription || matchesMaterials || matchesIncludes || 
            matchesBestFor || matchesLanguages || matchesSpiritual || matchesRitualSteps) {
          results.push({
            type: 'subservice',
            id: subService.id,
            name: subService.name,
            description: subService.description,
            image: subService.image,
            price: subService.price,
            serviceId: serviceId,
            rating: subService.rating,
            duration: subService.duration,
            bookingUrl: subService.bookingUrl
          });
        }
      });
    });

    // Sort results by relevance (exact matches first, then partial matches)
    results.sort((a, b) => {
      const aExactName = a.name.toLowerCase() === searchTerm;
      const bExactName = b.name.toLowerCase() === searchTerm;
      
      if (aExactName && !bExactName) return -1;
      if (!aExactName && bExactName) return 1;
      
      const aStartsWithName = a.name.toLowerCase().startsWith(searchTerm);
      const bStartsWithName = b.name.toLowerCase().startsWith(searchTerm);
      
      if (aStartsWithName && !bStartsWithName) return -1;
      if (!aStartsWithName && bStartsWithName) return 1;
      
      // Prioritize sub-services over main services for more specific results
      if (a.type === 'subservice' && b.type === 'service') return -1;
      if (a.type === 'service' && b.type === 'subservice') return 1;
      
      return a.name.localeCompare(b.name);
    });

    setSearchResults(results);
    setIsSearching(false);
    return results;
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  }, []);

  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      performSearch(query);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [performSearch]);

  return (
    <SearchContext.Provider value={{
      searchQuery,
      searchResults,
      isSearching,
      setSearchQuery: handleSetSearchQuery,
      performSearch,
      clearSearch
    }}>
      {children}
    </SearchContext.Provider>
  );
};